import { AbortedError } from 'src/common/api/extended-emerald-error';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { TenderType } from 'src/common/enumerations/tender-type';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { IRefundTenderGroup, ITender } from 'src/common/interfaces/dtos/ITender';
import { TenderExchangeActions } from 'src/common/redux/reducers/tender-exchange-reducer';
import {
  CartBalanceInstructions,
  CartResponse,
  CartWithBalance,
} from 'src/items-list/business-logic/service/interfaces/cart-response';
import { handleOnlineItemDeclinedOnTender } from 'src/online-items/business-logic/handle-online-item-decline';
import { handlePrintEndorsement } from 'src/tender-check/business-logic/handle-print-endorsement';
import { getEndorsementConfig } from 'src/tender-check/business-logic/services/get-endorsement-config';
import { CheckEndorsementViewProps } from 'src/tender-check/components/check-processing-types';
import { showManualCheckValidation } from 'src/tender-check/components/manual-check-validation';
import {
  ManualEndorseProps,
  showManualEndorsement,
} from 'src/tender-check/components/manual-endorsement';
import {
  MicrProcessingPage,
  showMicrProcessingView,
} from 'src/tender-check/components/micr-processing-view';
import { showRefundModal } from 'src/tendering/business-logic/handle-refund-actions';
import { balanceCart } from 'src/tendering/business-logic/services/balance-cart-service';
import { performTenderBalance } from 'src/tendering/business-logic/services/balance-cart-with-tenders-service';
import { closeTransaction } from 'src/tendering/business-logic/services/close-transaction-service';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import {
  CartContainItemsThatNotReadyToTender,
  OnlineItemActivationDeclined,
  ReceiptMissingData,
  TenderChangeAvailability,
  TenderEndorsementIsRequired,
  TenderMissingRequiredParameters,
} from '../../common/helpers/error-code';
import { ITenderAdd, PaymentUsage, RefundUsage } from '../../common/interfaces/dtos/IPreTenderAdd';
import { IReceiptArgs } from '../../common/interfaces/dtos/IReceiptArgs';
import { store } from '../../common/redux/configure-store';
import { TenderMenuActions } from '../../common/redux/reducers/tender-menu-reducer';
import { CheckEndorsementConfig, hasMICR } from '../../devices/micr-utilities';
import { handleDigitalReceiptPreference } from '../../receipt-options/business-logic/handle-receipt-preference';
import { applyCartResponse } from './common/apply-cart-response';
import { completeTransaction } from './complete-transaction';
import { refundAdd } from './service/refund-add';
import { tenderAdd } from './service/tender-add';

/**
 * Calls RTI to make a tender payment and updates redux state
 * if necessary.
 *
 * @param amount Value of payment
 * @param currencyCode Currency of the payment
 * @param tenderId Id that defines the tender type
 * @returns A value indicating whether the transaction is finalized
 */

export const makeTenderPayment = async (
  lineTender: ITenderAdd,
  isPrintReceipt?: boolean,
  receiptsArgs?: IReceiptArgs,
): Promise<void> => {
  const span = createSpan('make-tender-payment');
  const {
    transactionId,
    transactionCustomer: customer,
    isTransactionTotaled,
  } = store.getState().transaction;

  if (!receiptsArgs) {
    receiptsArgs = {
      printReceipt: isPrintReceipt,
      emailAddress: customer && customer.emailAddress,
      mobilePhoneNumber: customer && customer.mobilePhoneNumber,
    };
  }

  const receiptArgs_ = receiptsArgs;
  const balanceDue = store.getState().itemList.amountDue;

  await withSpinner(async () => {
    let addTenderCommand = tenderAdd;

    lineTender.lineId = store.getState().tenderMenu.unPaidPreTender?.lineId;

    if (balanceDue < 0 && lineTender.tender?.tenderType !== TenderType.Voucher) {
      addTenderCommand = refundAdd;
      lineTender.typeCode =
        lineTender.tender?.usage === PaymentUsage.ExchangeReceive
          ? RefundUsage.ExchangeReceive
          : RefundUsage.Refund;

      if (lineTender.amount && lineTender.amount.amount) {
        lineTender.amount.amount = Math.abs(lineTender.amount?.amount);
      }
    }

    let { callBalanceAfterTenderAdd } = getCurrentXLRConfig();
    // Instead of balancing we just want to return the cart for these cases
    if (
      (lineTender.tender?.tenderType === TenderType.Voucher && !isTransactionTotaled) ||
      lineTender.tender?.usage === PaymentUsage.ExchangePay
    ) {
      callBalanceAfterTenderAdd = false;
    }

    store.dispatch(TenderMenuActions.setUnPaidPreTender(lineTender));

    await addTenderCommand(transactionId, lineTender, receiptArgs_)
      .then(
        async (cartResponse: CartResponse): Promise<void> => {
          await applyCartResponse(cartResponse);
          store.dispatch(TenderMenuActions.clearUnPaidPreTender());
          await withSpinner(async () => {
            const cart = callBalanceAfterTenderAdd
              ? await balanceTransaction(transactionId, lineTender)
              : cartResponse;
            await completeTransaction(cart, isPrintReceipt);
          });
        },
      )
      .catch(err => handleTenderAddErrors(err, lineTender));
  });
  endSpan(span);
};

export const handleTenderAddErrors = async (err: any, lineTender: ITenderAdd): Promise<void> => {
  if (err.code === TenderMissingRequiredParameters && err.data?.checkValidatorName) {
    if (window.Cypress && lineTender.tender?.entryMethod === EntryMethod.Tapped) {
      lineTender.tender.entryMethod = EntryMethod.Automatic;
    }

    const firstAdd = err.data.requiredFields.every(field => field.validationMessage === 'Missing');
    if (hasMICR() && firstAdd) {
      showMicrProcessingView(lineTender, err);
    } else if (hasMICR() && !firstAdd) {
      showManualCheckValidation({
        tenderAdd: lineTender,
        chequeData: err,
        previousPageId: MicrProcessingPage,
      });
    } else {
      showManualCheckValidation({
        tenderAdd: lineTender,
        chequeData: err,
      });
    }
    //  Check process is not in a promise flow.  Can't be used to control tender add clean up.
    //  Throw an abortedError error to terminate tender add flow
    throw new AbortedError('ProcessCheck');
  } else if (err.code === TenderEndorsementIsRequired) {
    const { checkEndorsement } = getCurrentXLRConfig();
    let endorsementConfig: CheckEndorsementConfig = {};
    const tenderId = lineTender.tender?.tenderId ?? '';
    const amountDue = lineTender.amount?.amount ?? 0;
    if (checkEndorsement && tenderId) {
      await getEndorsementConfig(tenderId)
        .then(configData => {
          endorsementConfig = configData;
        })
        .catch(error => {
          console.error('Error during get endorsement configuration:', error);
        });
    }
    if (hasMICR()) {
      handlePrintEndorsement(lineTender, endorsementConfig);
    } else {
      const onlyShowBack = !endorsementConfig.receiptLinesFront;
      const screenData: CheckEndorsementViewProps = {
        amountDue,
        tenderDetails: lineTender,
        endorsementConfig,
      };
      const manualProps: ManualEndorseProps = {
        tenderId,
        screenData,
        onlyShowBack,
      };
      showManualEndorsement(manualProps);
    }
    throw new AbortedError('ProcessCheck');
  } else if (err.code === ReceiptMissingData) {
    await handleDigitalReceiptPreference(lineTender, err);
  } else if (
    err.code === CartContainItemsThatNotReadyToTender &&
    err.data?.errorDetails === OnlineItemActivationDeclined
  ) {
    await handleOnlineItemDeclinedOnTender(err);
  } else if (err.code === OnlineItemActivationDeclined) {
    await handleOnlineItemDeclinedOnTender(err);
  } else if (err.code === TenderChangeAvailability && !store.getState().tendering.isTendering) {
    throw {
      code: err.code,
      data: { lineId: lineTender.lineId },
    };
  } else {
    throw err;
  }
};

const getITenderObject = (
  balanceInstructions: CartBalanceInstructions[] | undefined,
): ITender[] => {
  const tenderArray: ITender[] = [];
  balanceInstructions?.forEach((tender, index) => {
    tenderArray[index] = {
      amount: tender.amount,
      description: tender.description,
      id: tender.tenderId,
      type: tender.tenderType as TenderType,
      usage: tender.usage as RefundUsage,
    };
  });
  return tenderArray;
};

const getTenderDetails = (
  balanceInstructions: CartBalanceInstructions[] | undefined,
): IRefundTenderGroup => {
  const refundTenderGroup: IRefundTenderGroup = { refundTender: [] };
  const validTenders = store.getState().tenderMenu.sellableTenders.rawTenders;
  const tenderList = getITenderObject(balanceInstructions);
  tenderList?.forEach((tender, index) => {
    const refundAmount = tender.amount;
    const tenderWithDetails = validTenders.find(i => i.id === tender.id);
    if (tenderWithDetails) {
      tenderList[index] = (tenderWithDetails as unknown) as ITender;
      tenderList[index].amount = refundAmount;
    }
  });
  refundTenderGroup.refundTender = tenderList;

  return refundTenderGroup;
};

export const balanceTransaction = async (
  transactionId: string,
  lineTender?: ITenderAdd,
): Promise<CartResponse> => {
  const balance: CartWithBalance = await balanceCart(transactionId);
  const isTBRtransaction =
    store.getState().posMode.returnMode.returnType === ReturnsType.TransactionBasedReturn;
  if (balance.balance.cartCanBeBalanced && isTBRtransaction) {
    showRefundModal(getTenderDetails(balance.balance.balanceInstructions));
  } else if (balance.balance.cartAlreadyBalanced) {
    return closeTransaction(transactionId);
  } else {
    const { isTenderExchange } = store.getState().tenderExchange;
    if (balance.balance.cartCanBeBalanced) {
      const balancedCart = await performTenderBalance(
        transactionId,
        balance.balance.balanceInstructions,
        lineTender || {},
      );
      await applyCartResponse(balancedCart);
      return balancedCart;
    } else if (balance.balance.cartAlreadyBalanced) {
      if (isTenderExchange) {
        store.dispatch(TenderExchangeActions.setTenderExchangeState(false));
      }
      return closeTransaction(transactionId);
    }
  }
  return balance.cart;
};
